import React, { useState, useEffect, useRef } from "react";
import { ethers } from "ethers";
import toast from "react-hot-toast";
import { useWallet } from "../context/WalletContext";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

const TokenAdmin = () => {
    const { tokenContract, readOnlyTokenContract, account } = useWallet();
    const initKeyRef = useRef("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);

    // Token info state
    const [tokenInfo, setTokenInfo] = useState({
        name: "",
        symbol: "",
        totalSupply: "0",
        balance: "0",
    });

    // Tax receivers state
    const [profitTaxReceiver, setProfitTaxReceiver] = useState("");

    // New tax receivers input state
    const [newProfitTaxReceiver, setNewProfitTaxReceiver] = useState("");

    // Buy enabled state
    const [buyEnabled, setBuyEnabled] = useState(false);

    // Tax rates input state
    const [buyTax, setBuyTax] = useState("");
    const [sellTax, setSellTax] = useState("");
    const [profitTax, setProfitTax] = useState("");
    const [currentTaxConfig, setCurrentTaxConfig] = useState({
        buyTax: "0",
        sellTax: "0",
        profitTax: "0",
    });

    // Max sell amount state
    const [maxSellAmount, setMaxSellAmount] = useState("");
    const [currentMaxSellAmount, setCurrentMaxSellAmount] = useState("0");

    // Sell cooldown state
    const [sellCooldown, setSellCooldown] = useState("");
    const [currentSellCooldown, setCurrentSellCooldown] = useState("0");

    // Exclude address state
    const [excludeAddress, setExcludeAddress] = useState("");

    // Transfer state
    const [recipient, setRecipient] = useState("");
    const [amount, setAmount] = useState("");

    // Burn amount state
    const [burnAmount, setBurnAmount] = useState("");

    // New owner state
    const [newOwner, setNewOwner] = useState("");
    const [adminList, setAdminList] = useState([]);

    useEffect(() => {
        if (!readOnlyTokenContract || !account) {
            initKeyRef.current = "";
            return;
        }
        const key = `${readOnlyTokenContract.target || readOnlyTokenContract.address || "token"}-${account.toLowerCase()}`;
        if (initKeyRef.current === key) return;
        initKeyRef.current = key;
        checkAdmin();
    }, [readOnlyTokenContract, account]);

    const checkAdmin = async () => {
        try {
            setLoading(true);
            const adminRole = await readOnlyTokenContract.ADMIN_ROLE();
            const hasAdminRole = await readOnlyTokenContract.hasRole(adminRole, account);
            setIsAdmin(hasAdminRole);

            if (hasAdminRole) {
                loadTokenInfo();
                loadTaxReceivers();
                loadBuyEnabled();
                loadTaxConfig();
                loadLimitConfig();
                loadAdminList();
            }
        } catch (error) {
            console.error("Error checking admin status:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadTokenInfo = async () => {
        if (!readOnlyTokenContract || !account) return;
        try {
            const [name, symbol, supply, bal] = await Promise.all([
                readOnlyTokenContract.name(),
                readOnlyTokenContract.symbol(),
                readOnlyTokenContract.totalSupply(),
                readOnlyTokenContract.balanceOf(account),
            ]);
            setTokenInfo({
                name,
                symbol,
                totalSupply: ethers.formatEther(supply),
                balance: ethers.formatEther(bal),
            });
        } catch (error) {
            console.error("Error loading token info:", error);
        }
    };

    const loadTaxReceivers = async () => {
        if (!readOnlyTokenContract) return;
        try {
            const profit = typeof readOnlyTokenContract.profitTaxReceiver === "function"
                ? await readOnlyTokenContract.profitTaxReceiver()
                : ZERO_ADDRESS;
            setProfitTaxReceiver(profit || ZERO_ADDRESS);
        } catch (error) {
            console.error("Error loading tax receivers:", error);
        }
    };

    const loadBuyEnabled = async () => {
        if (!readOnlyTokenContract) return;
        try {
            const enabled = await readOnlyTokenContract.buyEnabled();
            setBuyEnabled(enabled);
        } catch (error) {
            console.error("Error loading buy enabled status:", error);
        }
    };

    const loadTaxConfig = async () => {
        if (!readOnlyTokenContract) return;
        try {
            const [buy, sell, profit] = await Promise.all([
                readOnlyTokenContract.buyTax(),
                readOnlyTokenContract.sellTax(),
                readOnlyTokenContract.profitTax(),
            ]);
            setCurrentTaxConfig({
                buyTax: String(buy),
                sellTax: String(sell),
                profitTax: String(profit),
            });
        } catch (error) {
            console.error("Error loading tax config:", error);
        }
    };

    const loadLimitConfig = async () => {
        if (!readOnlyTokenContract) return;
        try {
            const [maxSell, cooldown] = await Promise.all([
                readOnlyTokenContract.maxSellAmount(),
                readOnlyTokenContract.sellCooldown(),
            ]);
            setCurrentMaxSellAmount(ethers.formatEther(maxSell));
            setCurrentSellCooldown(String(cooldown));
        } catch (error) {
            console.error("Error loading limit config:", error);
        }
    };

    const loadAdminList = async () => {
        if (!readOnlyTokenContract) return;
        try {
            const adminRole = await readOnlyTokenContract.ADMIN_ROLE();
            const candidates = [account, newOwner].filter((addr) => addr && ethers.isAddress(addr));
            const uniqCandidates = Array.from(new Set(candidates.map((addr) => addr.toLowerCase())));
            const checks = await Promise.all(
                uniqCandidates.map(async (addr) => {
                    try {
                        const has = await readOnlyTokenContract.hasRole(adminRole, addr);
                        return has ? addr : null;
                    } catch {
                        return null;
                    }
                })
            );
            const admins = checks.filter(Boolean);
            setAdminList(admins.length > 0 ? admins : (account ? [account.toLowerCase()] : []));
        } catch (error) {
            console.error("Error loading admin list:", error);
            if (account) setAdminList([account.toLowerCase()]);
        }
    };

    const handleTransaction = async (txPromise, toastId, successMsg) => {
        if (!tokenContract) {
            toast.error("合约未初始化，请检查钱包连接或网络");
            return false;
        }
        toast.loading("正在准备交易...", { id: toastId });
        try {
            const tx = await txPromise;
            toast.loading("交易已提交，等待上链...", { id: toastId });
            const receipt = await tx.wait();
            if (receipt && receipt.status === 1) {
                toast.success(successMsg, { id: toastId });
                return true;
            } else {
                toast.error("交易执行失败", { id: toastId });
                return false;
            }
        } catch (error) {
            console.error("Transaction error:", error);
            let errorMsg = error.reason || error.message || "交易失败";
            const errorCode = error.code || error.originalError?.code;
            if (errorCode === "ACTION_REJECTED" || errorCode === 4001) {
                errorMsg = "用户取消了交易";
            } else if (errorCode === "TRANSACTION_REPLACED") {
                if (error.cancelled) {
                    errorMsg = "交易已取消";
                } else {
                    toast.success(successMsg, { id: toastId });
                    return true;
                }
            } else if (error.message && error.message.includes("duplicate call detected")) {
                toast.success(successMsg, { id: toastId });
                return true;
            }
            toast.error(errorMsg, { id: toastId });
            return false;
        }
    };

    const handleSetTaxReceivers = async () => {
        if (!ethers.isAddress(newProfitTaxReceiver)) {
            toast.error("请输入有效的地址");
            return;
        }

        const inputCount = tokenContract?.interface?.getFunction?.("setTaxReceivers")?.inputs?.length || 1;
        const txPromise = inputCount === 1
            ? tokenContract.setTaxReceivers(newProfitTaxReceiver)
            : tokenContract.setTaxReceivers(ZERO_ADDRESS, ZERO_ADDRESS, newProfitTaxReceiver);

        const success = await handleTransaction(
            txPromise,
            "taxrecv",
            "税收地址设置成功"
        );
        if (success) {
            setNewProfitTaxReceiver("");
            loadTaxReceivers();
        }
    };

    const handleSetBuyEnabled = async () => {
        const success = await handleTransaction(
            tokenContract.setBuyEnabled(!buyEnabled),
            "buy",
            `买入开关已${buyEnabled ? "关闭" : "开启"}`
        );
        if (success) loadBuyEnabled();
    };

    const handleSetTaxRates = async () => {
        if (!buyTax || !sellTax || !profitTax) {
            toast.error("请输入完整的税率");
            return;
        }
        await handleTransaction(
            tokenContract.setTaxRates(buyTax, sellTax, profitTax),
            "tax",
            "税率设置成功"
        );
        loadTaxConfig();
    };

    const handleSetMaxSell = async () => {
        if (!maxSellAmount) {
            toast.error("请输入最大卖出量");
            return;
        }
        await handleTransaction(
            tokenContract.setMaxSellAmount(ethers.parseEther(maxSellAmount)),
            "maxsell",
            "最大卖出量设置成功"
        );
        loadLimitConfig();
    };

    const handleSetCooldown = async () => {
        if (!sellCooldown) {
            toast.error("请输入冷却时间");
            return;
        }
        await handleTransaction(
            tokenContract.setSellCooldown(sellCooldown),
            "cooldown",
            "冷却时间设置成功"
        );
        loadLimitConfig();
    };

    const handleSetExcluded = async (excluded) => {
        if (!ethers.isAddress(excludeAddress)) {
            toast.error("请输入有效的地址");
            return;
        }
        await handleTransaction(
            tokenContract.setExcludedFromTax(excludeAddress, excluded),
            "exclude",
            `地址已${excluded ? "添加到" : "从"}免税名单${excluded ? "" : "移除"}`
        );
    };

    const handleBurn = async () => {
        if (!burnAmount || Number(burnAmount) <= 0) {
            toast.error("请输入有效的金额");
            return;
        }
        if (!window.confirm("确定要销毁代币吗？此操作不可逆！")) {
            return;
        }
        const success = await handleTransaction(
            tokenContract.burn(ethers.parseEther(burnAmount)),
            "burn",
            "销毁成功"
        );
        if (success) {
            setBurnAmount("");
            loadTokenInfo();
        }
    };

    const handleTransferOwnership = async () => {
        if (!ethers.isAddress(newOwner)) {
            toast.error("请输入有效的地址");
            return;
        }
        if (!window.confirm("确定要转让权限吗？此操作不可逆！")) {
            return;
        }
        await handleTransaction(
            tokenContract.transferOwnership(newOwner),
            "ownership",
            "所有权转移成功"
        );
        loadAdminList();
    };

    const handleRenounceOwnership = async () => {
        if (!window.confirm("确定要丢弃合约权限吗？此操作不可逆！")) {
            return;
        }
        if (!window.confirm("再次确认：丢弃权限后将永久失去管理权限！")) {
            return;
        }
        const signerAddress = await tokenContract?.runner?.getAddress?.();
        if (!signerAddress) {
            toast.error("未检测到钱包地址");
            return;
        }
        const adminRole = await tokenContract.ADMIN_ROLE();
        const success = await handleTransaction(
            tokenContract.renounceRole(adminRole, signerAddress),
            "renounce",
            "管理员权限已丢弃"
        );
        if (success) {
            loadAdminList();
            checkAdmin();
        }
    };

    const formatNum = (val) => {
        const num = parseFloat(val);
        return isNaN(num) ? "0.00" : num.toFixed(2);
    };

    const formatBps = (bps) => `${bps} (${(Number(bps || 0) / 100).toFixed(2)}%)`;

    if (loading) {
        return <div className="loading">加载中...</div>;
    }

    if (!isAdmin) {
        return (
            <div className="card">
                <h2 className="card-title">代币管理</h2>
                <p style={ { color: "#ef4444", textAlign: "center", padding: "20px" } }>
                    您没有管理员权限
                </p>
            </div>
        );
    }

    return (
        <div>
            <div className="card">
                <h2 className="card-title">代币信息</h2>
                <div className="info-row">
                    <span className="info-label">代币名称</span>
                    <span className="info-value">{ tokenInfo.name }</span>
                </div>
                <div className="info-row">
                    <span className="info-label">代币符号</span>
                    <span className="info-value">{ tokenInfo.symbol }</span>
                </div>
                <div className="info-row">
                    <span className="info-label">总供应量</span>
                    <span className="info-value">
                        { formatNum(tokenInfo.totalSupply) } { tokenInfo.symbol }
                    </span>
                </div>
                <div className="info-row">
                    <span className="info-label">您的余额</span>
                    <span className="info-value">
                        { formatNum(tokenInfo.balance) } { tokenInfo.symbol }
                    </span>
                </div>
            </div>

            <div className="card">
                <h2 className="card-title">税收地址</h2>
                <div className="info-row">
                    <span className="info-label">买入税收地址</span>
                    <span className="info-value" style={ { fontFamily: "monospace", fontSize: "12px" } }>
                        { ZERO_ADDRESS }
                    </span>
                </div>
                <div className="info-row">
                    <span className="info-label">卖出税收地址</span>
                    <span className="info-value" style={ { fontFamily: "monospace", fontSize: "12px" } }>
                        { ZERO_ADDRESS }
                    </span>
                </div>
                <div className="info-row">
                    <span className="info-label">盈利税收地址</span>
                    <span className="info-value" style={ { fontFamily: "monospace", fontSize: "12px" } }>
                        { profitTaxReceiver }
                    </span>
                </div>

                <div className="form-group" style={ { marginTop: "16px" } }>
                    <label className="form-label">新盈利税收地址</label>
                    <input
                        type="text"
                        className="form-input"
                        value={ newProfitTaxReceiver }
                        onChange={ (e) => setNewProfitTaxReceiver(e.target.value) }
                        placeholder="0x..."
                    />
                </div>
                <button className="btn btn-primary" onClick={ handleSetTaxReceivers }>
                    设置税收地址
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">买入开关</h2>
                <div style={ { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" } }>
                    <span>
                        当前状态：
                        <strong style={ { color: buyEnabled ? "#9acd32" : "#ef4444" } }>
                            { buyEnabled ? "已开启" : "已关闭" }
                        </strong>
                    </span>
                    <button className="btn btn-primary" onClick={ handleSetBuyEnabled }>
                        { buyEnabled ? "关闭买入" : "开启买入" }
                    </button>
                </div>
            </div>

            <div className="card">
                <h2 className="card-title">设置税率</h2>
                <div className="info-row">
                    <span className="info-label">当前买入税率</span>
                    <span className="info-value">{ formatBps(currentTaxConfig.buyTax) }</span>
                </div>
                <div className="info-row">
                    <span className="info-label">当前卖出税率</span>
                    <span className="info-value">{ formatBps(currentTaxConfig.sellTax) }</span>
                </div>
                <div className="info-row">
                    <span className="info-label">当前盈利税率</span>
                    <span className="info-value">{ formatBps(currentTaxConfig.profitTax) }</span>
                </div>
                <div className="form-group">
                    <label className="form-label">买入税率(基点, 10000=100%)</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ buyTax }
                        onChange={ (e) => setBuyTax(e.target.value) }
                        placeholder="例如: 300 = 3%"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">卖出税率(基点)</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ sellTax }
                        onChange={ (e) => setSellTax(e.target.value) }
                        placeholder="例如: 500 = 5%"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">盈利税率(基点)</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ profitTax }
                        onChange={ (e) => setProfitTax(e.target.value) }
                        placeholder="例如: 200 = 2%"
                    />
                </div>
                <button className="btn btn-primary" onClick={ handleSetTaxRates }>
                    设置税率
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">设置最大卖出量</h2>
                <div className="info-row">
                    <span className="info-label">当前最大卖出量</span>
                    <span className="info-value">{ formatNum(currentMaxSellAmount) } { tokenInfo.symbol || "TOKEN" }</span>
                </div>
                <div className="form-group">
                    <label className="form-label">最大卖出量</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ maxSellAmount }
                        onChange={ (e) => setMaxSellAmount(e.target.value) }
                        placeholder="0.0"
                    />
                </div>
                <button className="btn btn-primary" onClick={ handleSetMaxSell }>
                    设置最大卖出量
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">设置卖出冷却时间</h2>
                <div className="info-row">
                    <span className="info-label">当前冷却时间</span>
                    <span className="info-value">{ currentSellCooldown } 秒</span>
                </div>
                <div className="form-group">
                    <label className="form-label">冷却时间(秒)</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ sellCooldown }
                        onChange={ (e) => setSellCooldown(e.target.value) }
                        placeholder="0"
                    />
                </div>
                <button className="btn btn-primary" onClick={ handleSetCooldown }>
                    设置冷却时间
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">免税名单</h2>
                <div className="form-group">
                    <label className="form-label">地址</label>
                    <input
                        type="text"
                        className="form-input"
                        value={ excludeAddress }
                        onChange={ (e) => setExcludeAddress(e.target.value) }
                        placeholder="0x..."
                    />
                </div>
                <div style={ { display: "flex", gap: "12px" } }>
                    <button className="btn btn-primary" onClick={ () => handleSetExcluded(true) }>
                        添加到免税名单
                    </button>
                    <button className="btn" style={ { background: "#555" } } onClick={ () => handleSetExcluded(false) }>
                        从免税名单移除
                    </button>
                </div>
            </div>

            <div className="card">
                <h2 className="card-title">销毁代币</h2>
                <div className="form-group">
                    <label className="form-label">销毁数量</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ burnAmount }
                        onChange={ (e) => setBurnAmount(e.target.value) }
                        placeholder="0.0"
                    />
                </div>
                <button className="btn btn-danger" onClick={ handleBurn }>
                    销毁
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">权限管理</h2>
                <div className="info-row">
                    <span className="info-label">当前管理员</span>
                    <span className="info-value">{ adminList.length } 个</span>
                </div>
                <div style={ { marginBottom: "12px" } }>
                    { adminList.length > 0 ? adminList.map((admin) => (
                        <div key={ admin } className="info-row">
                            <span className="info-label">管理员</span>
                            <span className="info-value" style={ { fontFamily: "monospace", fontSize: "12px" } }>
                                { admin }
                            </span>
                        </div>
                    )) : (
                        <div className="info-row">
                            <span className="info-label">管理员</span>
                            <span className="info-value">暂无数据</span>
                        </div>
                    ) }
                </div>
                <div className="form-group">
                    <label className="form-label">新管理员地址</label>
                    <input
                        type="text"
                        className="form-input"
                        value={ newOwner }
                        onChange={ (e) => setNewOwner(e.target.value) }
                        placeholder="0x..."
                    />
                </div>
                <button className="btn btn-primary" onClick={ handleTransferOwnership }>
                    转让权限
                </button>
                <div style={ { marginTop: "20px" } }>
                    <button className="btn btn-danger" onClick={ handleRenounceOwnership }>
                        丢弃所有权限（不可逆）
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TokenAdmin;
